// src/pages/IntakeHub/ExistingChildSheet.js
import React, { useEffect, useState } from 'react';
import DisabilityPicker from './DisabilityPicker';
import { listEligibleChildren, startCaseForChild, LANGUAGES, ageLabel } from './caseHistoryApi';

const NEEDS_LABEL = {
    dob: 'Date of birth to check',
    supervising_therapist: 'Supervising therapist',
};

export default function ExistingChildSheet({ therapists, onClose, onCreated }) {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chosen, setChosen] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;
        listEligibleChildren()
            .then(({ data }) => mounted && setChildren(data))
            .catch(() => mounted && setError('Could not load children.'))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    const choose = (child) => {
        setChosen(child);
        setError('');
        setForm({
            // Pre-filled, but the worker confirms each one with the guardian.
            date_of_birth: child.dob_verified ? child.date_of_birth : '',
            gender: child.gender || '',
            primary_language: child.primary_language || 'Hindi',
            supervising_therapist_id: '',
            disabilities: child.disabilities || [],
            disability_notes: '',
            guardian_name: child.guardian_name || '',
            guardian_phone: child.guardian_phone || '',
            guardian_email: child.guardian_email || '',
        });
    };

    const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
    const ready = form && form.date_of_birth && form.gender && form.primary_language && form.supervising_therapist_id;

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            const { data } = await startCaseForChild(chosen.id, {
                ...form,
                supervising_therapist_id: Number(form.supervising_therapist_id),
                disabilities: form.disabilities.length ? form.disabilities : null,
            });
            onCreated(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not start the case. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content ih-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="ih-row" style={{ marginBottom: 14 }}>
                    <h2 style={{ margin: 0 }}>{chosen ? `${chosen.name} — check the profile` : 'Start from an existing child'}</h2>
                    {chosen && <button className="btn btn-secondary" onClick={() => { setChosen(null); setForm(null); }}>← Back</button>}
                </div>

                {error && <div className="ih-note red" style={{ marginBottom: 12 }}>{error}</div>}

                {!chosen && (
                    loading ? <div className="ih-empty">Loading…</div> : (
                        <div className="ih-picklist">
                            {children.length === 0 && <div className="ih-empty">No children in this organisation yet.</div>}
                            {children.map((child) => (
                                <button
                                    key={child.id}
                                    className="ih-pick"
                                    disabled={!!child.open_case_id}
                                    onClick={() => choose(child)}
                                >
                                    <span>
                                        <span className="ih-pick-name">{child.name}</span>
                                        <span className="ih-sub"> · {ageLabel(child.age_months)} · {child.primary_language}</span>
                                    </span>
                                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {child.open_case_id
                                            ? <span className="ih-status active">Case already open</span>
                                            : child.needs.length === 0
                                                ? <span className="ih-status locked">Ready</span>
                                                : child.needs.map((n) => (
                                                    <span key={n} className="ih-status warn">{NEEDS_LABEL[n] || n}</span>
                                                ))}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )
                )}

                {chosen && form && (
                    <>
                        {!chosen.dob_verified && (
                            <div className="ih-note amber" style={{ marginBottom: 12 }}>
                                <strong>This date of birth was never confirmed with a guardian</strong>
                                {chosen.date_of_birth ? ` (on record: ${chosen.date_of_birth})` : ''}. Age decides the
                                milestone checks and what goes into the research export, so ask the guardian and enter it again.
                            </div>
                        )}

                        <div className="ih-grid2">
                            <div className="form-group">
                                <label>Date of birth <span className="ih-req">*</span></label>
                                <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
                            </div>
                            <div className="form-group">
                                <label>Gender <span className="ih-req">*</span></label>
                                <select value={form.gender} onChange={set('gender')}>
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div className="ih-grid2">
                            <div className="form-group">
                                <label>Primary language <span className="ih-req">*</span></label>
                                <select value={form.primary_language} onChange={set('primary_language')}>
                                    {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Supervising therapist <span className="ih-req">*</span></label>
                                <select value={form.supervising_therapist_id} onChange={set('supervising_therapist_id')}>
                                    <option value="">Select</option>
                                    {therapists.map((t) => <option key={t.id} value={t.id}>{t.username}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Disability (if any)</label>
                            <DisabilityPicker
                                value={form.disabilities}
                                onChange={(disabilities) => setForm({ ...form, disabilities })}
                            />
                        </div>

                        <div className="ih-grid2">
                            <div className="form-group">
                                <label>Guardian name</label>
                                <input value={form.guardian_name} onChange={set('guardian_name')} />
                            </div>
                            <div className="form-group">
                                <label>Guardian phone</label>
                                <input value={form.guardian_phone} onChange={set('guardian_phone')} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Guardian email</label>
                            <input type="email" value={form.guardian_email} onChange={set('guardian_email')} />
                        </div>

                        <div className="ih-note blue" style={{ marginBottom: 12 }}>
                            Older clinical entries on this profile (severity, diagnosis) stay as they are. There is no
                            record of who wrote them, so the Case History asks again with evidence.
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button className="btn btn-primary" disabled={!ready || saving} onClick={submit}>
                                {saving ? 'Starting…' : 'Confirm and start the case'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
